import { z } from 'zod';
import { schemas } from '../../generated/api-client';
import { TypedApiClient } from '../index';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// ====== Import schemas from generated code ======
const {
  CreateRevisionDto,
  RevisionFirestoreResponse,
  RevisionItemFirestoreResponse,
} = schemas;

// Re-export as local constants for backwards compatibility
export const RevisionResponseSchema = RevisionFirestoreResponse;
export const RevisionItemSchema = RevisionItemFirestoreResponse;
export const CreateRevisionSchema = CreateRevisionDto;
// UpdateRevisionDto derives from response via PartialType; use partial of response for now
export const UpdateRevisionSchema = RevisionFirestoreResponse.partial();

// ====== Comparison Schemas (custom, not generated) ======

export const RevisionComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: RevisionFirestoreResponse.nullable(),
  firestoreData: z.record(z.unknown()).nullable().optional(),
  matches: z.boolean(),
  differences: z.array(z.object({
    field: z.string(),
    supabaseValue: z.unknown(),
    firestoreValue: z.unknown(),
  })).optional(),
  foundInSupabase: z.boolean().optional(),
  foundInFirestore: z.boolean().optional(),
  cascading: z.record(z.unknown()).optional(),
}).passthrough();

// ====== Type Exports ======

export type RevisionResponse = z.infer<typeof RevisionResponseSchema>;
export type CreateRevision = z.infer<typeof CreateRevisionSchema>;
export type UpdateRevision = z.infer<typeof UpdateRevisionSchema>;
export type RevisionItem = z.infer<typeof RevisionItemSchema>;
export type RevisionComparisonResult = z.infer<typeof RevisionComparisonResultSchema>;

/**
 * Create revisions API factory
 */
export function createRevisionsApi(client: TypedApiClient) {
  return {
    /**
     * Get revisions with optional filters
     */
    getRevisions: (params?: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
      includeDeleted?: boolean;
    }): Promise<RevisionResponse[]> => {
      return client.get('/revisions', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(RevisionResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/revisions` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getRevisionsWindow: (
      params: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
      includeDeleted?: 'true';
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof RevisionResponseSchema>>> => {
      return client.get('/revisions/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(RevisionResponseSchema),
      });
    },

    /**
     * Get a single revision by ID
     */
    getRevisionById: (id: string): Promise<RevisionResponse> => {
      return client.get(apiPath`/revisions/${id}`, {
        responseSchema: RevisionResponseSchema,
      });
    },

    /**
     * Create a new revision
     */
    createRevision: (data: CreateRevision): Promise<RevisionResponse> => {
      return client.post('/revisions', {
        data,
        requestSchema: CreateRevisionSchema,
        responseSchema: RevisionResponseSchema,
      });
    },

    /**
     * Update a revision
     */
    updateRevision: (id: string, data: UpdateRevision): Promise<RevisionResponse> => {
      return client.patch(apiPath`/revisions/${id}`, {
        data,
        requestSchema: UpdateRevisionSchema,
        responseSchema: RevisionResponseSchema,
      });
    },

    /**
     * Delete a revision (soft-delete)
     */
    deleteRevision: (id: string) => {
      return client.delete(apiPath`/revisions/${id}`, {
        responseSchema: z.object({ message: z.string() }),
      });
    },

    /**
     * Compare revision with Firestore for dual-write verification
     */
    compareWithFirestore: (firestoreId: string): Promise<RevisionComparisonResult> => {
      return client.get(apiPath`/revisions/compare/${firestoreId}`, {
        responseSchema: RevisionComparisonResultSchema,
      });
    },
  };
}
